/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import {
  ProviderInterface,
  PersonInterface,
  UserInterface,
  VitalsInterface,
  PharmacyInterface,
  FacilityInterface,
  ResourceInterface,
  InsuranceInterface,
  CaseInterface,
  LanguageEnum,
  CurrencyEnum,
  UserRolesEnum,
} from "@malmur-technologies/medicalsystem-shared";
import { SelectArrayType, EntityTypeEnum } from "types/form.enum";
import * as Yup from "yup";

import i18n from "i18n";
import { TFunction } from "i18next";
import { ExtendedPersonInterface } from "interfaces/parent.person.interface";
import { FormikBag, FormikErrors, FormikTouched, FormikValues } from "formik";
import { CountryEnum } from "@malmur-technologies/medicalsystem-shared";

/**
 * Maps a UserRolesEnum to its corresponding EntityTypeEnum.
 * Used to determine which entity type (patient/provider) should be checked
 * when validating if a person already has a specific role.
 * 
 * @param userRole - The user role to map
 * @returns The corresponding EntityTypeEnum (PATIENT or PROVIDER) or null if not applicable
 */
export function getUserRoleEntityType(userRole: UserRolesEnum): EntityTypeEnum | null {
  if (userRole === UserRolesEnum.PATIENT) {
    return EntityTypeEnum.PATIENT;
  }

  if (
    userRole === UserRolesEnum.DOCTOR ||
    userRole === UserRolesEnum.NURSE ||
    userRole === UserRolesEnum.HEAD_NURSE
  ) {
    return EntityTypeEnum.PROVIDER;
  }

  return null;
}

/**
 * Converts an enum object to an array of select options with translated labels
 * @param _enum - The enum object to convert
 * @param t - The translation function from i18next
 * @returns An array of select options with label and value properties
 */
export const getSelectOptions = (
  _enum: { [x: string]: string | number },
  t: TFunction<"translation">
): SelectArrayType[] => {
  const arrayLength = Object.keys(_enum).length;
  if (Object.keys(_enum).some((key) => !isNaN(Number(key)))) {
    return Object.keys(_enum)
      .slice(arrayLength / 2, arrayLength)
      .map((key) => {
        return { label: key, value: _enum[key].toString() };
      });
  } else {
    return Object.keys(_enum).map((key) => {
      return { label: t("Enums." + key), value: _enum[key].toString() };
    });
  }
};

/**
 * Validates password strength and checks against common patterns
 * @param t - The translation function from i18next
 * @param password - The password to validate
 * @param username - Optional username to check against password
 * @param lastname - Optional lastname to check against password
 * @param name - Optional name to check against password
 * @returns True if password is valid, or a string with error messages if invalid
 */
export const passwordValidation = (t: TFunction<"translation">, password?: string, username?: string, lastname?: string, name?: string) => {
  const errors: string[] = [];
  if (!password || password.length < 14) errors.push(t("formUtils.min14Chars"));
  if (!/[A-Z]/.test(password || "")) errors.push(t("formUtils.passwordMustHaveUppercase"));
  if (!/[a-z]/.test(password || "")) errors.push(t("formUtils.passwordMustHaveLowercase"));
  if (!/[0-9]/.test(password || "")) errors.push(t("formUtils.passwordMustHaveNumber"));
  if (!/[@$!%*?&]/.test(password || "")) errors.push(t("formUtils.passwordMustHaveSpecialChar"));
  const wordsToCheck = [
    ...(username ? [username] : []),
    ...(name ? name.split(" ") : []),
    ...(lastname ? lastname.split(" ") : []),
  ].map(w => w.toLowerCase());
  if (wordsToCheck.some(word => word && password.toLowerCase().includes(word))) {
    errors.push(t("formUtils.passwordValidation"));
  }

  if (errors.length) {
    return errors.join(`\n`);
  }
  return true;
}

/**
 * Creates Yup validation schema for user form with password requirements
 * @param t - The translation function from i18next
 * @param username - Optional username for password validation
 * @param lastname - Optional lastname for password validation
 * @param name - Optional name for password validation
 * @returns A Yup validation schema for UserInterface
 */
export const userFormYUPValidation = (t: TFunction<"translation">, username?: string, lastname?: string, name?: string, simplificatedClinic?: boolean) =>
  Yup.object<UserInterface>().shape(
    {
      username: !simplificatedClinic ? Yup.string()
        .required(t("formUtils.usernameIsRequired"))
        .min(4, t("formUtils.min4Chars")) : Yup.string().notRequired(),
      email: !simplificatedClinic ? Yup.string().required(t("formUtils.emailIsRequired")).email(t("formUtils.invalidEmail")) : Yup.string().notRequired(),
      auth: Yup.object().shape({
        password: Yup.string()
          .required(t("formUtils.passwordsrequired"))
          .test("password-robustness", function (value) {
            const result = passwordValidation(t, value, username, lastname, name);
            if (result !== true) {
              return this.createError({ message: result });
            }
            return true;
          }),
      }),
      repeatPassword: Yup.string()
        .required(t("formUtils.passwordsMustMatch"))
        .min(8, t("formUtils.min8Chars"))
        .oneOf([Yup.ref("auth.password"), null], t("formUtils.passwordsMustMatch")),
    });
/**
 * Creates Yup validation schema for user form without password requirements
 * @param t - The translation function from i18next
 * @returns A Yup validation schema for UserInterface without password validation
 */
export const userFormNoPassYUPValidation = (t: TFunction<"translation">, simplificatedClinic?: boolean) =>
  Yup.object<UserInterface>().shape(
    {
      username: simplificatedClinic ? Yup.string().notRequired() : Yup.string()
        .required(t("formUtils.usernameIsRequired"))
        .min(4, t("formUtils.min4Chars")),
      email: simplificatedClinic ? Yup.string().notRequired() : Yup.string().required(t("formUtils.emailIsRequired")).email(t("formUtils.invalidEmail")),
      auth: Yup.object().shape({
        password: Yup.string().notRequired(),
      }),
      repeatPassword: Yup.string().notRequired(),
    });

/**
 * Creates Yup validation schema for person form with conditional identity validation
 * @param t - The translation function from i18next
 * @param minorUserChecked - Optional boolean indicating if user is a minor (affects identity requirement)
 * @param simplificatedClinic - Optional boolean for simplified clinic mode
 * @param country - Optional country code (MEXICO or US) for identity format validation
 * @returns A Yup validation schema for PersonInterface
 */
export const personFormYUPValidation = (t: TFunction<"translation">, minorUserChecked?: boolean, simplificatedClinic?: boolean, country?: string) =>
  Yup.object<PersonInterface>().shape({
    name: Yup.string().required(t("formUtils.nameIsRequired")).min(2, t("formUtils.min2Chars")),
    lastname: Yup.string()
      .required(t("formUtils.lastNameIsRequired"))
      .min(2, t("formUtils.min2Chars")),
    phones: Yup.array().of(
      Yup.object().shape({
        number: Yup.string().notRequired(),
        idx: Yup.string().notRequired(),
      })
    ),
    identity: Yup.string().when([], () => {
      if (simplificatedClinic || minorUserChecked) {
        return Yup.string().notRequired();
      } else {
        const isMexico = country === CountryEnum.MEXICO;
        const length = isMexico ? 18 : 9;
        const fieldName = isMexico ? 'CURP' : 'SSN';
        return Yup.string()
          .required(t("formUtils.identity"))
          .length(length, isMexico ? t("formUtils.identityCURPLength", { length, fieldName }) : t("formUtils.identitySSNLength", { length, fieldName }))
          .matches(/^[A-Za-z0-9]+$/, t("formUtils.identityAlphanumeric"))
      }
    }),
    sex: Yup.string().required(t("formUtils.sexAssignedIsRequired")),
    previousName: Yup.string().notRequired().min(2, t("formUtils.min2Chars")),
    address: Yup.string().notRequired(),
    city: Yup.string().notRequired().min(2, t("formUtils.min2Chars")),
    state: Yup.string().notRequired().min(2, t("formUtils.min2Chars")),
    county: Yup.string().notRequired().min(2, t("formUtils.min2Chars")),
    zip: Yup.string().notRequired().min(2, t("formUtils.min2Chars")),
    country: Yup.string().notRequired().min(2, t("formUtils.min2Chars")),
    emergencyContacts: Yup.array().notRequired(),
    mi: Yup.string().notRequired().min(1, t("formUtils.min1Chars")),
    prefix: Yup.string().notRequired(),
    suffix: Yup.string().notRequired(),
    ethnicity: Yup.string().notRequired().min(2, t("formUtils.min2Chars")),
  });

/**
 * Creates Yup validation schema for parent/guardian person form
 * @param t - The translation function from i18next
 * @param simplificatedClinic - Optional boolean for simplified clinic mode
 * @param country - Optional country code (MEXICO or US) for identity format validation
 * @returns A Yup validation schema for ExtendedPersonInterface
 */
export const personParentFormYUPValidation = (t: TFunction<"translation">, simplificatedClinic?: boolean, country?: string) =>
  Yup.object<ExtendedPersonInterface>().shape({
    name: Yup.string().required(t("formUtils.nameIsRequired")).min(2, t("formUtils.min2Chars")),
    lastname: Yup.string()
      .required(t("formUtils.lastNameIsRequired"))
      .min(2, t("formUtils.min2Chars")),
    identity: !simplificatedClinic ? Yup.string()
      .required(t("formUtils.identity"))
      .length(country === CountryEnum.MEXICO ? 18 : 9, country === CountryEnum.MEXICO ? t("formUtils.identityCURPLength", { length: 18, fieldName: 'CURP' }) : t("formUtils.identitySSNLength", { length: 9, fieldName: 'SSN' }))
      .matches(/^[A-Za-z0-9]+$/, t("formUtils.identityAlphanumeric")) : Yup.string().notRequired(),
    sex: Yup.string().required(t("formUtils.sexAssignedIsRequired")),
    phone: Yup.string().notRequired(),
    username: Yup.string().notRequired(),
    email: Yup.string().notRequired().email(t("formUtils.invalidEmail")),
    address: Yup.string().notRequired(),
    city: Yup.string().notRequired().min(2, t("formUtils.min2Chars")),
    state: Yup.string().notRequired().min(2, t("formUtils.min2Chars")),
    county: Yup.string().notRequired().min(2, t("formUtils.min2Chars")),
    zip: Yup.string().notRequired().min(2, t("formUtils.min2Chars")),
    country: Yup.string().notRequired().min(2, t("formUtils.min2Chars")),
  });

/**
 * Creates Yup validation schema for patient-specific form fields
 * @param t - The translation function from i18next
 * @param country - Optional country code (MEXICO or US) for identity format validation
 * @returns A Yup validation schema for patient information
 */
export const patientFormYUPValidation = (t: TFunction<"translation">, country?: string) =>
  Yup.object<PersonInterface>().shape({
    accountNumber: Yup.number().notRequired(),
    ssn: Yup.string()
      .notRequired()
      .test('ssn-length', country === CountryEnum.MEXICO ? t("formUtils.identityRFCLength", { length: 13, fieldName: 'RFC' }) : t("formUtils.identitySSNLength", { length: 9, fieldName: 'SSN' }), (value) => {
        if (!value) return true;
        const length = value.length;
        const expectedLength = country === CountryEnum.MEXICO ? 13 : 9;
        return length === expectedLength;
      })
      .matches(/^[A-Za-z0-9]+$/, {
        message: t("formUtils.identityAlphanumeric"),
        excludeEmptyString: true
      }),
    historyConsent: Yup.boolean().required(),
    releaseInformation: Yup.boolean().required(),
  });

/**
 * Creates Yup validation schema for doctor/provider form
 * @param t - The translation function from i18next
 * @returns A Yup validation schema for ProviderInterface
 */
/**
 * Creates Yup validation schema for doctor/provider form
 * @param t - The translation function from i18next
 * @param country - Optional country code (MEXICO or US) for DEA/PGR validation
 * @returns A Yup validation schema for ProviderInterface
 */
export const DoctorFormYUPValidation = (t: TFunction<"translation">, country?: string) =>
  Yup.object<ProviderInterface>().shape({
    medicalLicense: Yup.string()
      .required(t("formUtils.medicalLicenseRequired"))
      .min(7, t("formUtils.min7Chars"))
      .max(15, t("formUtils.max15Chars")),
    specialty: Yup.string().notRequired(),
    deaRegistration: Yup.string().when([], () => {
      // DEA for US (format: 2 letters + 7 numbers), PGR for Mexico
      if (country === CountryEnum.MEXICO) {
        // PGR Mexico: allow alphanumeric up to 20 chars (it hasn't a fixed format)
        return Yup.string().notRequired().max(20, t("formUtils.max20Chars"));
      } else {
        // DEA Registration US: 2 letters + 7 numbers
        return Yup.string().notRequired().matches(/^[A-Z]{2}\d{7}$/, t("formUtils.invalidDEANumberFormat"));
      }
    }),
  });

/**
 * Creates Yup validation schema for vital signs form
 * @param t - The translation function from i18next
 * @returns A Yup validation schema for VitalsInterface
 */
export const vitalsFormYUPValidation = (t: TFunction<"translation">) =>
  Yup.object<VitalsInterface>().shape({
    registrationDate: Yup.date().required(t("formUtils.registrationDateIsRequired")),
    note: Yup.string().max(2048, t("formUtils.noteMustBeMost2048")).notRequired(),
    bp: Yup.string().max(10, t("formUtils.bpMustBeMost10")).matches(/^\d{1,3}\/\d{1,3}$/, t("formUtils.bpMustMatchPattern")).notRequired(),
    hr: Yup.number().notRequired(),
    rr: Yup.number().notRequired(),
    temp: Yup.number().notRequired(),
    oxy: Yup.number().notRequired(),
    wt: Yup.number().notRequired(),
    wtPercentage: Yup.number().notRequired(),
    ht: Yup.number().notRequired(),
    htPercentage: Yup.number().notRequired(),
    bmi: Yup.number().notRequired(),
    bmiPercentage: Yup.number().notRequired(),
    painScale: Yup.number().notRequired().max(10, t("formUtils.painScaleMustBeMost")),
    patientId: Yup.string().notRequired(),
  });

export const userFormConfigBase = {
  username: Yup.string()
    .required(i18n.t("formUtils.usernameIsRequired"))
    .min(6, i18n.t("formUtils.min6Chars")),
  email: Yup.string()
    .required(i18n.t("formUtils.emailIsRequired"))
    .email(i18n.t("formUtils.invalidEmail")),
  password: Yup.string().notRequired().min(6, i18n.t("formUtils.min6Chars")),
  repeatPassword: Yup.string().notRequired().min(6, i18n.t("formUtils.min6Chars")),
};

export const providerFormConfig = {
  ...userFormConfigBase,
  phone: Yup.string().notRequired().min(10, i18n.t("formUtils.phoneNoValid")),
  specialty: Yup.string().notRequired().min(2, i18n.t("formUtils.min2Chars")),
  medicalLicense: Yup.string()
    .required(i18n.t("formUtils.medicalLicenseIsRequired"))
    .min(2, i18n.t("formUtils.min2Chars")),
  deaRegistration: Yup.string()
    .required(i18n.t("formUtils.DEANumberIsRequired"))
    .matches(/^[A-Z]{2}\d{7}$/, i18n.t("formUtils.invalidDEANumberFormat")),
};

export const appointmentFormConfig = (t: (key: string, options?: any) => string, nonBillable: boolean) => Yup.object().shape({
  arrivalDateTime: Yup.date().required(t("formUtils.timeNotBeEmpty")),
  provider: Yup.object().shape({
    id: Yup.string().required(t("formUtils.doctorRequired")),
  }),
  patient: Yup.object().shape({
    id: Yup.string().required(t("formUtils.patientRequired")),
  }),
  visitType: Yup.string().required(t("formUtils.visitTypeRequired")),
  facility: Yup.object().shape({
    id: Yup.string().required(t("formUtils.facilityRequired")),
  }),
  resource: Yup.object().shape({
    id: Yup.string().required(t("formUtils.resourceRequired")),
  }),
  visitStatus: Yup.string().notRequired(),
  diagnosis: Yup.string().notRequired(),
  email: Yup.string().required(t("formUtils.emailIsRequired")).email(t("formUtils.invalidEmail")),
  roomNumber: Yup.number().required(t("formUtils.roomNumberRequired")),
  reason: Yup.string().required(t("formUtils.reasonRequired")),
  patientInsurance: Yup.string().when([], () => {
    if (nonBillable) {
      return Yup.object().shape({
        id: Yup.string().notRequired(),
      }).notRequired()
    } else {
      return Yup.object().shape({
        id: Yup.string().required(t("formUtils.policyRequired")),
      }).required(t("formUtils.selectAnOption"))
    }
  }),
});

export const officeVisitFormConfig = (t: (key: string, options?: any) => string) =>
  Yup.object().shape({
    arrivalDateTime: Yup.string().required(t("formUtils.appointmentTimeCannotBeEmpty")),
    duration: Yup.number().notRequired().max(240, t("formUtils.durationTooLong")),
    roomNumber: Yup.string().notRequired().min(2, t("formUtils.roomNumber")),
    visitOfficeStatus: Yup.string().required(t("formUtils.selectAnOption")),
  });

/// Yup validation decrapeted ///

export const patientFormConfig = {
  ...userFormConfigBase,
  sex: Yup.string().notRequired(),
  dateOfBirth: Yup.string().notRequired(),
  phone: Yup.string().notRequired().min(10, i18n.t("formUtils.phoneNoValid")),
  historyConsent: Yup.string().notRequired(),
  releaseInformation: Yup.string().notRequired(),
  previousName: Yup.string().notRequired().min(2, i18n.t("formUtils.min2Chars")),
  address: Yup.string().notRequired().min(6, i18n.t("formUtils.min6Chars")),
  city: Yup.string().notRequired().min(2, i18n.t("formUtils.min2Chars")),
  state: Yup.string().notRequired().min(6, i18n.t("formUtils.min6Chars")),
  county: Yup.string().notRequired().min(6, i18n.t("formUtils.min6Chars")),
  zip: Yup.string().notRequired().min(5, i18n.t("formUtils.zipCodeInvalid")),
  country: Yup.string().notRequired().min(6, i18n.t("formUtils.min6Chars")),
  secondaryPhone: Yup.string().notRequired(),
};

/**
 * Creates Yup validation schema for pharmacy form
 * @param t - The translation function from i18next
 * @returns A Yup validation schema for PharmacyInterface
 */
export const pharmacyFormYUPValidation = (t: TFunction<"translation">) =>
  Yup.object<PharmacyInterface>().shape({
    displayName: Yup.string()
      .required(t("formUtils.nameIsRequired"))
      .min(2, t("formUtils.min2Chars")),
    storeName: Yup.string()
      .required(t("formUtils.nameIsRequired"))
      .min(2, t("formUtils.min2Chars")),
    email: Yup.string().notRequired().email(t("formUtils.invalidEmail")),
    address: Yup.string().required(t("formUtils.addressRequired")).min(6, t("formUtils.min6Chars")),
    address2: Yup.string().notRequired().min(6, t("formUtils.min6Chars")),
    city: Yup.string().required(t("formUtils.cityRequired")).min(2, t("formUtils.min2Chars")),
    state: Yup.string().required(t("formUtils.stateRequired")).min(6, t("formUtils.min6Chars")),
    county: Yup.string().required(t("formUtils.countyRequired")).min(6, t("formUtils.min6Chars")),
    zip: Yup.string().required(t("formUtils.zipRequired")).min(5, t("formUtils.zipCodeInvalid")),
    country: Yup.string().notRequired().min(6, t("formUtils.min6Chars")),
    phone: Yup.string().required(t("formUtils.phoneNoValid")),
    fax: Yup.string().notRequired().transform((value) => (value && value.trim() !== "" ? value : null)).min(10, t("formUtils.phoneNoValid")),
    ext: Yup.string()
      .notRequired()
      .min(1, t("formUtils.min1Chars"))
      .max(5, t("formUtils.max5Chars")),
  });

/**
 * Creates Yup validation schema for insurance company form
 * @param t - The translation function from i18next
 * @returns A Yup validation schema for InsuranceInterface
 */
export const insuranceFormYUPValidation = (t: TFunction<"translation">) =>
  Yup.object<InsuranceInterface>().shape({
    name: Yup.string().required(t("formUtils.nameIsRequired")).min(2, t("formUtils.min2Chars")),
    typeScheme: Yup.string()
      .required(t("formUtils.typeSchemaRequired"))
      .min(2, t("formUtils.min2Chars")),
    payerId: Yup.string().required(t("formUtils.payerIdRequired")).min(2, t("formUtils.min2Chars")),
    email: Yup.string().notRequired().email(t("formUtils.invalidEmail")),
    address: Yup.string().required(t("formUtils.addressRequired")).min(6, t("formUtils.min6Chars")),
    city: Yup.string().required(t("formUtils.cityRequired")).min(2, t("formUtils.min2Chars")),
    state: Yup.string().required(t("formUtils.stateRequired")).min(2, t("formUtils.min2Chars")),
    zip: Yup.string().required(t("formUtils.zipRequired")).min(5, t("formUtils.zipCodeInvalid")),
    country: Yup.string().notRequired().min(2, t("formUtils.min2Chars")),
    phone: Yup.string().required(t("formUtils.phoneNoValid")),
    termsPayment: Yup.string()
      .required(t("formUtils.termsPaymentRequired"))
      .min(2, t("formUtils.min2Chars")),
    requirements: Yup.string()
      .required(t("formUtils.requirementsRequired"))
      .min(2, t("formUtils.min2Chars")),
  });

/**
 * Creates Yup validation schema for facility form
 * @param t - The translation function from i18next
 * @returns A Yup validation schema for FacilityInterface
 */
export const facilityFormYUPValidation = (t: TFunction<"translation">) =>
  Yup.object<FacilityInterface>().shape({
    name: Yup.string().required(t("formUtils.nameIsRequired")).min(2, t("formUtils.min2Chars")),
    code: Yup.string().required(t("formUtils.codeIsRequired")).min(2, t("formUtils.min2Chars")),
    email: Yup.string().notRequired().email(t("formUtils.invalidEmail")),
    address: Yup.string().notRequired().min(6, t("formUtils.min6Chars")),
    city: Yup.string().notRequired().min(2, t("formUtils.min2Chars")),
    state: Yup.string().notRequired().min(2, t("formUtils.min2Chars")),
    zip: Yup.string().notRequired().min(5, t("formUtils.zipCodeInvalid")),
    country: Yup.string().notRequired().min(6, t("formUtils.min6Chars")),
    phone: Yup.string().notRequired(),
    fax: Yup.string().notRequired(),
  });

/**
 * Creates Yup validation schema for resource form
 * @param t - The translation function from i18next
 * @returns A Yup validation schema for ResourceInterface
 */
export const resourceFormYUPValidation = (t: TFunction<"translation">) =>
  Yup.object<ResourceInterface>().shape({
    name: Yup.string().required(t("formUtils.nameIsRequired")).min(2, t("formUtils.min2Chars")),
    code: Yup.string().notRequired().min(2, t("formUtils.min2Chars")),
  });

/**
 * Creates Yup validation schema for case form
 * @param t - The translation function from i18next
 * @returns A Yup validation schema for CaseInterface
 */
export const caseFormYUPValidation = (t: TFunction<"translation">) =>
  Yup.object<CaseInterface>().shape({
    label: Yup.string().required(t("formUtils.labelRequired")).min(2, t("formUtils.min2Chars")),
    typeCase: Yup.string().notRequired(),
    comment: Yup.string().notRequired(),
    status: Yup.string().required(t("formUtils.statusRequired")),
    patient: Yup.object().shape({
      id: Yup.string().required(t("formUtils.patientRequired")),
    }),
    provider: Yup.object().shape({
      id: Yup.string().required(t("formUtils.doctorRequired")),
    }),
  });

/**
 * Ensures a phone number starts with a '+' symbol
 * @param phoneNumber - The phone number to validate
 * @returns The phone number with a '+' prefix if not already present
 */
export function validatePhoneInputSymbol(phoneNumber: string): string {
  const regex = /\+/;
  if (!regex.test(phoneNumber)) {
    return "+" + phoneNumber;
  } else {
    return phoneNumber;
  }
}

/**
 * Creates Yup validation schema for pricing form with tax amount fields
 * @param t - The translation function that accepts a key and optional parameters
 * @returns A Yup validation schema for pricing data
 */
export const pricingFormYUPValidation = (t: (key: string, options?: any) => string) =>
  Yup.object().shape({
    name: Yup.string()
      .trim()
      .required(t("formUtils.nameRequired"))
      .max(255, t("formUtils.nameMax", { max: 255 })),

    price: Yup.number()
      .typeError(t("formUtils.priceMustBeNumber"))
      .required(t("formUtils.priceRequired"))
      .min(0, t("formUtils.priceMin", { min: 0 }))
      .max(999999999999.99, t("formUtils.priceMax", { max: 999999999999.99 })),

    description: Yup.string()
      .trim()
      .required(t("formUtils.descriptionRequired"))
      .max(255, t("formUtils.descriptionMax", { max: 255 })),

    taxAmountType: Yup.string(),

    taxAmount: Yup.number()
      .typeError(t("formUtils.taxAmountMustBeNumber"))
      .min(0, t("formUtils.taxAmountMin", { min: 0 }))
      .max(999999999999.99, t("formUtils.taxAmountMax", { max: 999999999999.99 }))
      .nullable(),

    alternativeTaxAmountType: Yup.string(),

    alternativeTaxAmount: Yup.number()
      .typeError(t("formUtils.alternativeTaxAmountMustBeNumber"))
      .min(0, t("formUtils.alternativeTaxAmountMin", { min: 0 }))
      .max(999999999999.99, t("formUtils.alternativeTaxAmountMax", { max: 999999999999.99 }))
      .nullable(),
  });

/**
 * Creates Yup validation schema for payer form with conditional fields based on mode
 * @param t - The translation function that accepts a key and optional parameters
 * @param isInsuranceMode - Boolean indicating if form is in insurance mode (affects required fields)
 * @returns A Yup validation schema for payer data
 */
export const payerFormYUPValidation = (
  t: (key: string, options?: any) => string,
  isInsuranceMode: boolean
) =>
  Yup.object().shape({
    name: Yup.string()
      .trim()
      .required(t("formUtils.nameRequired"))
      .max(255, t("formUtils.nameMax", { max: 255 })),

    lastname: Yup.string()
      .trim()
      .when([], {
        is: () => !isInsuranceMode,
        then: (schema) =>
          schema
            .required(t("formUtils.lastnameRequired"))
            .max(255, t("fomrUtils.lastnameMax", { max: 255 })),
        otherwise: (schema) => schema.strip(),
      }),

    age: Yup.number().when([], {
      is: () => !isInsuranceMode,
      then: (schema) =>
        schema
          .typeError(t("formUtils.ageType"))
          .min(0, t("formUtils.ageMin", { min: 0 }))
          .max(120, t("formUtils.ageMax", { max: 120 }))
          .required(t("formUtils.ageRequired")),
      otherwise: (schema) => schema.strip(),
    }),

    phone: Yup.string()
      .trim()
      .required(t("formUtils.phoneRequired"))
      .max(50, t("formUtils.phoneMax", { max: 50 })),

    state: Yup.string()
      .trim()
      .required(t("formUtils.stateRequired"))
      .max(255, t("formUtils.stateMax", { max: 255 })),
  });

export const paymentFormYUPValidation = (t: (key: string, options?: any) => string) =>
  Yup.object().shape({
    payer: Yup.object().shape({
      id: Yup.string().required(t("formUtils.payerRequired")),
    }),
    posted: Yup.number()
      .required(t("formUtils.postedRequired"))
      .min(0.01, t("formUtils.postedMinimum"))
      .max(999999999.99, t("formUtils.postedMaximum")),
    payerType: Yup.string().required(t("formUtils.payerTypeRequired")),
    paymentMethod: Yup.string().required(t("formUtils.paymentMethodRequired")),
    currency: Yup.string().required(t("formUtils.currencyRequired")),
  });

/**
 * Checks if a Formik form has validation errors in touched fields
 * @param values - The form values from Formik
 * @param errors - The form errors object from Formik
 * @param touched - The touched fields object from Formik
 * @returns An error message string if there are errors in touched fields, or null if valid
 */
export const checkFormValidity = (
  values: FormikValues,
  errors: FormikErrors<FormikValues>,
  touched: FormikTouched<FormikValues>
) => {
  const hasErrors = Object.keys(errors).length > 0;
  const hasTouchedFields = Object.keys(touched).length > 0;

  if (hasErrors && hasTouchedFields) {
    return i18n.t("formUtils.allRequiredFields");
  }
  return null;
}

/**
 * Automatically marks all fields with validation errors as touched.
 * Use this in a useEffect to highlight required fields in red automatically.
 * @param values - The form values from Formik
 * @param errors - The form errors object from Formik
 * @param touched - The touched fields object from Formik
 * @param setFieldTouched - Formik's setFieldTouched function
 */
export const autoTouchErrorFields = (
  values: FormikValues,
  errors: FormikErrors<FormikValues>,
  touched: FormikTouched<FormikValues>,
  setFieldTouched: (field: string, touched: boolean, shouldValidate?: boolean) => void
) => {
  if (checkFormValidity(values, errors, touched)) {
    const markFieldTouched = (obj: any, prefix = '') => {
      Object.keys(obj).forEach((key) => {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
          markFieldTouched(obj[key], fullKey);
        } else {
          if (!touched[fullKey]) {
            setFieldTouched(fullKey, true, true);
          }
        }
      });
    };
    markFieldTouched(errors);
  }
};

export const generalSettingsFormYUPValidation = (t: TFunction<"translation">) =>
  Yup.object().shape({
    country: Yup.string()
      .oneOf(Object.values(CountryEnum) as string[], t("generalSettingsModal.validation.invalidCountry"))
      .required(t("generalSettingsModal.validation.countryRequired")),
    language: Yup.string()
      .oneOf(Object.values(LanguageEnum) as string[], t("generalSettingsModal.validation.invalidLanguage"))
      .required(t("generalSettingsModal.validation.languageRequired")),
    currency: Yup.string()
      .oneOf(Object.values(CurrencyEnum) as string[], t("generalSettingsModal.validation.invalidCurrency"))
      .required(t("generalSettingsModal.validation.currencyRequired")),
    defaultAppointmentDuration: Yup.number()
      .min(5, t("generalSettingsModal.validation.durationMin"))
      .max(480, t("generalSettingsModal.validation.durationMax"))
      .required(t("generalSettingsModal.validation.durationRequired")),
    videochatEnabledTime: Yup.number()
      .min(0, t("generalSettingsModal.validation.videochatTimeMin"))
      .max(1440, t("generalSettingsModal.validation.videochatTimeMax"))
      .nullable()
      .optional(),
    videochatEnabledAfterAppointmentTime: Yup.number()
      .min(0, t("generalSettingsModal.validation.videochatTimeMin"))
      .max(1440, t("generalSettingsModal.validation.videochatTimeMax"))
      .nullable()
      .optional(),
  });